import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api, type User } from "../lib/api";
import { LoadingButton, InlineLoading } from "../components/Spinner";
import PasswordInput from "../components/PasswordInput";
import Avatar from "../components/Avatar";

export default function Account() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const me = useQuery({ queryKey: ["me"], queryFn: () => api.me() });
  const user = me.data?.user ?? null;
  const invitesQ = useQuery({ queryKey: ["my-invites"], queryFn: () => api.myInvites() });

  if (!user) return null;

  return (
    <div className="h-full overflow-auto scroll-pane p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <h1 className="text-2xl font-semibold tracking-tight">Account</h1>

        <ProfileCard user={user} onSaved={() => qc.invalidateQueries({ queryKey: ["me"] })} />

        <PasswordCard hasPassword={user.hasPassword} />

        <MfaCard onChanged={() => qc.invalidateQueries({ queryKey: ["me"] })} />

        <ThemeCard user={user} onChanged={() => qc.invalidateQueries({ queryKey: ["me"] })} />

        <PendingInvitesCard
          invites={invitesQ.data?.invites ?? []}
          onChanged={() => {
            qc.invalidateQueries({ queryKey: ["my-invites"] });
            qc.invalidateQueries({ queryKey: ["workspaces"] });
          }}
        />

        <div className="card p-5">
          <LogoutButton onSignOut={() => nav("/login")} />
        </div>
      </div>
    </div>
  );
}

function ProfileCard({ user, onSaved }: { user: User; onSaved: () => void }) {
  const [name, setName] = useState(user.name ?? "");
  const save = useMutation({
    mutationFn: () => api.updateMe({ name }),
    onSuccess: onSaved,
  });

  const fileRef = useRef<HTMLInputElement>(null);
  const upload = useMutation({
    mutationFn: (file: File) => api.avatars.uploadMe(file),
    onSuccess: onSaved,
  });
  const removeAvatar = useMutation({
    mutationFn: () => api.avatars.removeMe(),
    onSuccess: onSaved,
  });

  const src = user.avatarUpdatedAt ? api.avatars.userUrl(user.id, user.avatarUpdatedAt) : null;

  return (
    <form
      className="card p-5 space-y-4"
      onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
    >
      <h2 className="font-semibold">Profile</h2>
      <div className="flex items-center gap-4">
        <Avatar src={src} name={user.name} email={user.email} size={64} />
        <div className="flex flex-col gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload.mutate(f);
              if (fileRef.current) fileRef.current.value = "";
            }}
            className="hidden"
          />
          <div className="flex gap-2">
            <LoadingButton type="button" className="btn" pending={upload.isPending} onClick={() => fileRef.current?.click()}>
              Upload photo
            </LoadingButton>
            {user.avatarUpdatedAt && (
              <LoadingButton type="button" className="btn-danger" pending={removeAvatar.isPending} onClick={() => removeAvatar.mutate()}>
                Remove
              </LoadingButton>
            )}
          </div>
          <p className="text-xs muted-strong">PNG, JPG, GIF, WebP, or SVG. Up to 5 MB.</p>
          {upload.isError && <p className="text-xs text-danger">{String((upload.error as Error).message)}</p>}
        </div>
      </div>
      <div className="space-y-2">
        <label className="label">Email</label>
        <input className="input" value={user.email} disabled />
      </div>
      <div className="space-y-2">
        <label className="label">Display name</label>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
      </div>
      <div className="flex justify-end">
        <LoadingButton type="submit" className="btn-primary" pending={save.isPending} pendingLabel="Saving…">
          Save
        </LoadingButton>
      </div>
    </form>
  );
}

function PasswordCard({ hasPassword }: { hasPassword: boolean }) {
  const [current, setCurrent] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const save = useMutation({
    mutationFn: () => api.updatePassword({ currentPassword: current || undefined, password: pw }),
    onSuccess: () => { setDone(true); setCurrent(""); setPw(""); },
    onError: (e) => setErr(String((e as Error).message ?? "Couldn't update")),
  });
  return (
    <form
      className="card p-5 space-y-3"
      onSubmit={(e) => { e.preventDefault(); setErr(null); setDone(false); save.mutate(); }}
    >
      <h2 className="font-semibold">{hasPassword ? "Change password" : "Set a password"}</h2>
      {hasPassword && (
        <div className="space-y-2">
          <label className="label">Current password</label>
          <PasswordInput value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
        </div>
      )}
      <div className="space-y-2">
        <label className="label">New password</label>
        <PasswordInput minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" />
      </div>
      {err && <p className="text-xs text-danger">{err}</p>}
      {done && <p className="text-xs text-good">Updated.</p>}
      <div className="flex justify-end">
        <LoadingButton type="submit" className="btn-primary" pending={save.isPending} pendingLabel="Saving…">
          {hasPassword ? "Update password" : "Set password"}
        </LoadingButton>
      </div>
    </form>
  );
}

function PendingInvitesCard({ invites, onChanged }: { invites: { id: string; workspace_name: string; inviter_name: string | null; inviter_email: string }[]; onChanged: () => void }) {
  if (!invites.length) return null;
  return (
    <div className="card p-5 space-y-3">
      <h2 className="font-semibold">Pending invites</h2>
      <ul className="space-y-2">
        {invites.map((i) => <InviteRow key={i.id} invite={i} onChanged={onChanged} />)}
      </ul>
    </div>
  );
}

function InviteRow({ invite, onChanged }: { invite: { id: string; workspace_name: string; inviter_name: string | null; inviter_email: string }; onChanged: () => void }) {
  const accept = useMutation({ mutationFn: () => api.acceptInvite(invite.id), onSuccess: onChanged });
  const reject = useMutation({ mutationFn: () => api.rejectInvite(invite.id), onSuccess: onChanged });
  return (
    <li className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface-2 p-3">
      <div className="text-sm">
        <div className="font-medium">{invite.workspace_name}</div>
        <div className="muted-strong text-xs">From {invite.inviter_name ?? invite.inviter_email}</div>
      </div>
      <div className="flex gap-2">
        <LoadingButton className="btn" pending={reject.isPending} onClick={() => reject.mutate()}>Decline</LoadingButton>
        <LoadingButton className="btn-primary" pending={accept.isPending} onClick={() => accept.mutate()}>Accept</LoadingButton>
      </div>
    </li>
  );
}

function LogoutButton({ onSignOut }: { onSignOut: () => void }) {
  const qc = useQueryClient();
  const logout = useMutation({
    mutationFn: () => api.logout(),
    onSuccess: () => {
      qc.setQueryData(["me"], { user: null });
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" });
      onSignOut();
    },
  });
  return (
    <LoadingButton className="btn-danger" pending={logout.isPending} onClick={() => logout.mutate()} pendingLabel="Signing out…">
      Sign out
    </LoadingButton>
  );
}

function MfaCard({ onChanged }: { onChanged: () => void }) {
  const status = useQuery({ queryKey: ["mfa", "me"], queryFn: () => api.mfa.status() });
  const [enrolling, setEnrolling] = useState<{ secret: string; otpauth: string } | null>(null);
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const start = useMutation({
    mutationFn: () => api.mfa.start(),
    onSuccess: (r) => { setEnrolling(r); setCode(""); setErr(null); },
  });
  const confirm = useMutation({
    mutationFn: () => api.mfa.confirm(code.trim()),
    onSuccess: (r) => { setRecoveryCodes(r.recoveryCodes); setEnrolling(null); status.refetch(); onChanged(); },
    onError: (e) => setErr(parseMfaError(e)),
  });
  const disable = useMutation({
    mutationFn: () => api.mfa.disable(code.trim()),
    onSuccess: () => { setRecoveryCodes(null); setCode(""); status.refetch(); onChanged(); },
    onError: (e) => setErr(parseMfaError(e)),
  });

  if (!status.data) return <div className="card p-5"><InlineLoading /></div>;

  return (
    <div className="card p-5 space-y-3">
      <h2 className="font-semibold">Two-factor authentication</h2>
      <p className="text-sm muted">
        Adds an extra step to sign-in using an authenticator app (Google Authenticator, 1Password, Authy, Bitwarden, …).
      </p>

      {!status.data.enabled && !enrolling && (
        <LoadingButton className="btn-primary" pending={start.isPending} onClick={() => start.mutate()}>
          Set up two-factor
        </LoadingButton>
      )}

      {enrolling && (
        <div className="rounded-md border border-border bg-surface-2 p-4 space-y-4">
          <p className="text-sm">Scan this code with your authenticator app, or enter the secret manually.</p>
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <div className="bg-white p-3 rounded">
              <QRCodeSVG value={enrolling.otpauth} size={160} level="M" />
            </div>
            <div className="flex-1 space-y-2 min-w-0">
              <div>
                <label className="label">Secret</label>
                <pre className="code-block break-all">{enrolling.secret}</pre>
              </div>
              <div>
                <label className="label">Enter the 6-digit code from your app</label>
                <input
                  className="input input-mono mt-1"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="123 456"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                />
              </div>
              {err && <p className="text-xs text-danger">{err}</p>}
              <div className="flex gap-2 justify-end">
                <button className="btn" onClick={() => { setEnrolling(null); setErr(null); }}>Cancel</button>
                <LoadingButton className="btn-primary" pending={confirm.isPending} disabled={code.trim().length < 6} onClick={() => confirm.mutate()}>
                  Confirm + enable
                </LoadingButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {status.data.enabled && !enrolling && (
        <div className="rounded-md border border-good/40 bg-good/10 p-3 text-sm space-y-3">
          <p>✓ Two-factor is enabled. Sign-in requires a code from your authenticator.</p>
          <div className="space-y-2">
            <label className="label">Enter a code (or recovery code) to disable</label>
            <div className="flex gap-2">
              <input
                className="input input-mono"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123 456 or xxxx-xxxx"
                inputMode="text"
              />
              <LoadingButton className="btn-danger" pending={disable.isPending} disabled={!code.trim()} onClick={() => disable.mutate()}>
                Disable
              </LoadingButton>
            </div>
            {err && <p className="text-xs text-danger">{err}</p>}
          </div>
        </div>
      )}

      {recoveryCodes && (
        <div className="rounded-md border border-warn bg-warn/10 p-3 text-sm space-y-2">
          <p className="font-semibold">Save your recovery codes</p>
          <p className="muted">
            Each code works once if you lose access to your authenticator. They won't be shown again.
          </p>
          <pre className="code-block">{recoveryCodes.join("\n")}</pre>
          <button className="btn" onClick={() => setRecoveryCodes(null)}>I've saved them</button>
        </div>
      )}
    </div>
  );
}

function parseMfaError(e: unknown): string {
  const m = String((e as Error).message ?? "");
  if (m.includes("bad_code") || m.includes("bad_mfa_code")) return "That code didn't match — try the next one your authenticator shows.";
  if (m.includes("no_pending")) return "Restart enrollment — the pending secret was discarded.";
  return "Something went wrong.";
}

const JSON_THEMES = [
  { id: "default",         label: "Default" },
  { id: "monokai",         label: "Monokai" },
  { id: "dracula",         label: "Dracula" },
  { id: "github_light",    label: "GitHub Light" },
  { id: "solarized_dark",  label: "Solarized Dark" },
  { id: "nord",            label: "Nord" },
  { id: "one_dark",        label: "One Dark" },
];

function ThemeCard({ user, onChanged }: { user: User; onChanged: () => void }) {
  const [theme, setTheme] = useState(user.jsonTheme ?? "default");
  const save = useMutation({
    mutationFn: () => api.updateMe({ json_theme: theme }),
    onSuccess: onChanged,
  });
  return (
    <div className="card p-5 space-y-3">
      <h2 className="font-semibold">JSON colour theme</h2>
      <p className="text-sm muted">Applies to response bodies and other syntax-highlighted JSON.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {JSON_THEMES.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTheme(t.id)}
            className={
              "rounded-md border p-2 text-left text-sm " +
              (theme === t.id ? "border-accent bg-accent-soft" : "border-border bg-surface-2 hover:bg-surface-3")
            }
          >
            <div className="font-medium">{t.label}</div>
            <ThemePreview theme={t.id} />
          </button>
        ))}
      </div>
      <div className="flex justify-end">
        <LoadingButton type="button" className="btn-primary" pending={save.isPending} onClick={() => save.mutate()}>
          Save
        </LoadingButton>
      </div>
    </div>
  );
}

function ThemePreview({ theme }: { theme: string }) {
  return (
    <div className={`mt-2 rounded p-2 text-[10px] mono leading-tight json-theme-${theme}`}>
      <span className="jt-punc">{`{`}</span>
      <br />
      &nbsp;&nbsp;<span className="jt-key">&quot;id&quot;</span><span className="jt-punc">: </span><span className="jt-num">42</span><span className="jt-punc">,</span>
      <br />
      &nbsp;&nbsp;<span className="jt-key">&quot;name&quot;</span><span className="jt-punc">: </span><span className="jt-str">&quot;ada&quot;</span>
      <br />
      <span className="jt-punc">{`}`}</span>
    </div>
  );
}
